import { useTranslation } from 'react-i18next';
import SceneLayout from '@/components/scene/SceneLayout';
import Aperture from '@/components/scene/Aperture';
import Poche from '@/components/scene/Poche';
import atriumFigure from '@/assets/scene/about-atrium-figure.webp';

const teamMembers = [
  {
    key: 'soliman',
    name: 'Mohamed Soliman',
    linkedin: 'https://www.linkedin.com/in/mohamed-soliman-04981061/',
  },
  {
    key: 'shaker',
    name: 'Ahmed Shaker, MCIPS',
    linkedin: 'https://www.linkedin.com/in/ahmed-shaker-mcips-5793b3161/',
  },
];

const FEATURES = ['ai', 'contacts', 'channels', 'analytics'] as const;

export default function About() {
  const { t } = useTranslation();

  const renderPane = (member: (typeof teamMembers)[number]) => (
    <article className="pane">
      <Poche ratio="3 / 4" caption={t(`about.team.${member.key}.fig`)} />
      <div className="grid gap-1 border-t border-[var(--ink)] pt-4">
        <h2 className="text-[1.35rem] font-medium">{member.name}</h2>
        <p className="mono">{t('about.cofounder')}</p>
        <a href={member.linkedin} target="_blank" rel="noreferrer" className="link-line self-start">
          {t('about.linkedin')}
        </a>
      </div>
    </article>
  );

  return (
    <SceneLayout>
      {/* Manifesto — the statement fills the frame, the figure stays small */}
      <section className="wrap pt-[clamp(48px,12vh,140px)]">
        <div className="relative pb-[clamp(140px,24vh,260px)]" data-rv="rack">
          <p className="mono mb-6">{t('about.label')}</p>
          <h1 className="display max-w-[12ch] mb-10">{t('about.title')}</h1>
          <p className="lede max-w-[34ch]">
            {t('about.manifesto_a')} <em className="em">{t('about.manifesto_em')}</em> {t('about.manifesto_b')}
          </p>
          <Poche
            src={atriumFigure}
            alt={t('about.figure_alt')}
            caption={t('about.fig_figure')}
            className="absolute bottom-0 end-[6%] w-[clamp(120px,14vw,170px)]"
            ratio="4 / 5"
          />
        </div>
      </section>

      {/* Two panes and a mullion */}
      <section className="wrap section">
        <p className="mono mb-8">{t('about.team_title')}</p>
        <Aperture ticks label={t('about.team_label')}>
          <div className="panes" data-rv="split">
            {renderPane(teamMembers[0])}
            <span className="mullion" aria-hidden="true" />
            {renderPane(teamMembers[1])}
          </div>
        </Aperture>
      </section>

      {/* What we do */}
      <section className="wrap pb-[var(--section)]">
        <div className="split-golden">
          <div className="grid gap-4 content-start">
            <p className="mono">S-04</p>
            <h2 className="display-m">{t('about.what_title')}</h2>
          </div>
          <ol data-rv="cut">
            {FEATURES.map((f, i) => (
              <li
                key={f}
                data-i
                style={{ ['--i' as string]: i }}
                className="grid grid-cols-[3.5rem_1fr] gap-4 py-6 border-b border-[var(--hairline-strong)] first:border-t first:border-t-[var(--ink)]"
              >
                <span className="mono">{String(i + 1).padStart(2, '0')}</span>
                <div className="grid gap-2">
                  <h3 className="text-[17px] font-medium">{t(`about.features.${f}.title`)}</h3>
                  <p className="body text-[15px]">{t(`about.features.${f}.desc`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </SceneLayout>
  );
}
